import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, Copy, Check, Mail, Download, Home, AlertCircle, RefreshCw, FileText } from "lucide-react";
import { useAIAssist, type SummarizeResult } from "@/hooks/useAIAssist";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

interface PostMeetingSummaryProps {
  meetingId: string;
  meetingTitle: string;
  onClose?: () => void;
  source?: "chat" | "transcript" | "manual";
}

export function PostMeetingSummary({
  meetingId,
  meetingTitle,
  onClose,
  source = "transcript",
}: PostMeetingSummaryProps) {
  const navigate = useNavigate();
  const { call, loading, lastError, clearError } = useAIAssist();
  const [result, setResult] = useState<SummarizeResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const generate = useCallback(async () => {
    clearError();
    const r = await call<SummarizeResult>({
      mode: "summarize",
      meetingId,
      source,
    });
    if (r) setResult(r);
  }, [call, clearError, meetingId, source]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("meeting_summaries")
        .select("summary, key_points, action_items")
        .eq("meeting_id", meetingId)
        .maybeSingle();
      if (data) {
        setResult({
          summary: data.summary,
          key_points: (data.key_points as string[]) || [],
          action_items: (data.action_items as any[]) || [],
        });
      } else {
        await generate();
      }
    })();
  }, [meetingId]);

  const formatPlain = () => {
    if (!result) return "";
    return [
      `Meeting: ${meetingTitle}`,
      "",
      "Summary:",
      result.summary,
      "",
      "Key points:",
      ...result.key_points.map((p) => `• ${p}`),
      "",
      "Action items:",
      ...result.action_items.map((a) => `• ${a.owner}: ${a.task}`),
    ].join("\n");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(formatPlain());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copied to clipboard");
  };

  const handleDownload = () => {
    const blob = new Blob([formatPlain()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${meetingTitle.replace(/[^\w-]+/g, "_")}-summary.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 48;
      const maxWidth = pageWidth - margin * 2;
      let y = margin;

      const ensureSpace = (lineHeight: number) => {
        if (y + lineHeight > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      const writeWrapped = (text: string, size: number, opts?: { bold?: boolean; color?: [number, number, number]; gap?: number }) => {
        doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
        doc.setFontSize(size);
        if (opts?.color) doc.setTextColor(...opts.color);
        else doc.setTextColor(30, 30, 30);
        const lines = doc.splitTextToSize(text, maxWidth);
        const lh = size * 1.35;
        for (const line of lines) {
          ensureSpace(lh);
          doc.text(line, margin, y);
          y += lh;
        }
        if (opts?.gap) y += opts.gap;
      };

      // Header band
      doc.setFillColor(99, 102, 241);
      doc.rect(0, 0, pageWidth, 72, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Meeting Recap", margin, 36);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text("Generated by MeetHub AI", margin, 56);
      y = 100;

      writeWrapped(meetingTitle, 16, { bold: true, gap: 4 });
      writeWrapped(new Date().toLocaleString(), 10, { color: [120, 120, 120], gap: 16 });

      writeWrapped("Summary", 13, { bold: true, color: [99, 102, 241], gap: 4 });
      writeWrapped(result.summary || "—", 11, { gap: 14 });

      if (result.key_points.length > 0) {
        writeWrapped("Key Points", 13, { bold: true, color: [99, 102, 241], gap: 4 });
        result.key_points.forEach((p) => writeWrapped(`•  ${p}`, 11));
        y += 8;
      }

      if (result.action_items.length > 0) {
        writeWrapped("Action Items", 13, { bold: true, color: [99, 102, 241], gap: 4 });
        result.action_items.forEach((a) => writeWrapped(`→  ${a.owner}: ${a.task}`, 11));
      }

      // Footer page numbers
      const total = doc.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${total}`, pageWidth - margin, pageHeight - 20, { align: "right" });
        doc.text("MeetHub.Live", margin, pageHeight - 20);
      }

      doc.save(`${meetingTitle.replace(/[^\w-]+/g, "_")}-recap.pdf`);
      toast.success("PDF recap downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Failed to generate PDF");
    }
  };

  const handleEmail = async () => {
    const recipients = emailRecipients
      .split(/[,\s]+/)
      .map((e) => e.trim())
      .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));

    if (recipients.length === 0) {
      toast.error("Add at least one valid email address");
      return;
    }

    try {
      setSending(true);
      const { error } = await supabase.functions.invoke("send-meeting-summary", {
        body: { meetingId, recipients },
      });
      if (error) throw error;
      setSent(true);
      toast.success(`Summary sent to ${recipients.length} recipient${recipients.length > 1 ? "s" : ""}`);
    } catch (e: any) {
      toast.error(e.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="w-full max-w-3xl mx-auto shadow-xl animate-fade-in">
      <CardHeader className="border-b">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-xl">AI meeting summary</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">{meetingTitle}</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {loading === "summarize" ? (
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
            <div className="h-4 bg-muted rounded animate-pulse w-full" />
            <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
            <div className="flex items-center justify-center pt-4 text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating insights…
            </div>
          </div>
        ) : !result && lastError ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">Couldn't generate summary</p>
                <p className="text-sm text-muted-foreground">
                  {lastError.kind === "rate_limit"
                    ? "AI is busy right now. Wait a few seconds and retry."
                    : lastError.kind === "credits"
                      ? "AI credits exhausted. Add credits in workspace settings to generate summaries."
                      : lastError.kind === "network"
                        ? "Couldn't reach the AI service. Check your connection."
                        : lastError.message || "Something went wrong."}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")}>
                Back to dashboard
              </Button>
              <Button size="sm" onClick={generate} disabled={lastError.kind === "credits"}>
                <RefreshCw className="h-4 w-4 mr-1.5" /> Try again
              </Button>
            </div>
          </div>
        ) : !result ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No summary available.
          </div>
        ) : (
          <>
            <section>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Summary
              </h3>
              <p className="leading-relaxed text-foreground">{result.summary}</p>
            </section>

            {result.key_points.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Key points
                </h3>
                <ul className="space-y-2">
                  {result.key_points.map((p, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="text-primary mt-1">•</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {result.action_items.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Action items
                </h3>
                <ul className="space-y-2">
                  {result.action_items.map((a, i) => (
                    <li
                      key={i}
                      className="flex gap-3 p-3 rounded-lg bg-muted/40 border border-border/60"
                    >
                      <span className="text-primary mt-0.5">→</span>
                      <div>
                        <p className="text-sm">
                          <span className="font-semibold">{a.owner}</span>{" "}
                          <span className="text-muted-foreground">·</span> {a.task}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="border-t pt-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-4 w-4" /> Download
                </Button>
                <Button size="sm" onClick={handleDownloadPdf}>
                  <FileText className="h-4 w-4" /> Download PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/dashboard")}
                  className="ml-auto"
                >
                  <Home className="h-4 w-4" /> Back to dashboard
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipients" className="text-sm">
                  Send via email
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="recipients"
                    placeholder="name@example.com, another@example.com"
                    value={emailRecipients}
                    onChange={(e) => setEmailRecipients(e.target.value)}
                    disabled={sending || sent}
                  />
                  <Button onClick={handleEmail} disabled={sending || sent}>
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : sent ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    {sent ? "Sent" : "Send"}
                  </Button>
                </div>
              </div>
            </section>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default PostMeetingSummary;
